import { Alert, Breadcrumb, flash, HeaderToolbar } from "@xola/ui-kit";
import {
    ClientSideRowModelModule,
    ColumnAutoSizeModule,
    CustomFilterModule,
    DateFilterModule,
    ModuleRegistry,
    NumberFilterModule,
    RowSelectionModule,
    TextFilterModule,
} from "ag-grid-community";
import { SetFilterModule } from "ag-grid-enterprise";
import React, { FC } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Permission, Report, ReportObject } from "types";
import { ColumnLayout } from "../../components/Layout/ColumnLayout";
import { Loader } from "../../components/Loader";
import { AbandonedReport } from "../../components/Reports/CustomReports/Reports/AbandonedReport";
import { CapacityReport } from "../../components/Reports/CustomReports/Reports/CapacityReport";
import { MerchandiseGrossSalesReport } from "../../components/Reports/CustomReports/Reports/MerchandiseGrossSalesReport";
import { PurchasesReport } from "../../components/Reports/CustomReports/Reports/PurchasesReport";
import { QuestionnaireReport } from "../../components/Reports/CustomReports/Reports/QuestionnaireReport";
import { ReservationsByListingReport } from "../../components/Reports/CustomReports/Reports/ReservationsByListingReport";
import { ReservationsReport } from "../../components/Reports/CustomReports/Reports/ReservationsReport";
import { TicketRedemptionReport } from "../../components/Reports/CustomReports/Reports/TicketRedemptionReport";
import { TransactionReport } from "../../components/Reports/CustomReports/Reports/TransactionReport";
import { SuggestedReport, useSuggestedReports } from "../../components/Reports/CustomReports/SuggestedReports";
import { useProducts } from "../../hooks/resources/useProduct";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useResource } from "../../hooks/useResource";
import { Page } from "../Page";
import { PageNotFoundIllustration } from "../PageNotFound";
import { CustomersReport } from "@/components/Reports/CustomReports/Reports/CustomersReport";
import { MembershipsReport } from "@/components/Reports/CustomReports/Reports/MembershipsReport";
import { MerchandiseInventoryReport } from "@/components/Reports/CustomReports/Reports/MerchandiseInventoryReport";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    SetFilterModule,
    TextFilterModule,
    NumberFilterModule,
    ColumnAutoSizeModule,
    CustomFilterModule,
    RowSelectionModule,
    DateFilterModule,
]);
export const CustomReportPage = () => {
    const seller = useSeller();
    const history = useHistory();
    const params = useQueryParams();
    const { isGranted } = useAuthorization();
    const { id } = useParams<{ id?: ID }>();
    const suggestedReports = useSuggestedReports();

    useProducts(); // Almost every report needs experiences, packages etc, so just pre-load it

    const { data: report, isLoading } = useResource<Report>(["sellers", seller.id, "reports", id]);

    const handleRedirect = () => {
        history.push("/reports/customReports");
    };

    const reportObject = params.get("object") as ReportObject;

    // Saved reports are fetched by id and bypass the permission filter in `useSuggestedReports`
    const requestedObject = report?.object ?? reportObject;
    if (requestedObject === ReportObject.CUSTOMERS && !isGranted(Permission.CUSTOMERS_EXPORT)) {
        flash.danger("You are not authorized to access this page.");
        history.push("/");
        return null;
    }

    const currentReport = report ?? makeReport(reportObject, suggestedReports);
    if (!currentReport) {
        handleRedirect();
        return null;
    }

    const Component = reportMap[currentReport.object] ?? MissingReport;

    return (
        <Page isDynamicTitle={!!currentReport.title} title={currentReport.title}>
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb className="min-w-[330px] truncate border-r-0">
                        <Breadcrumb.Item>Reports</Breadcrumb.Item>
                        <Breadcrumb.Item
                            onClick={isGranted(Permission.REPORTS_CUSTOM_REPORTS_VIEW) ? handleRedirect : undefined}
                        >
                            Custom Reports
                        </Breadcrumb.Item>
                        {currentReport.title}
                    </HeaderToolbar.Breadcrumb>
                </HeaderToolbar>

                {isLoading ? <Loader /> : Component ? <Component report={currentReport} /> : null}
            </ColumnLayout>
        </Page>
    );
};

const reportMap: { [K in ReportObject]?: FC<{ report: Report }> } = {
    [ReportObject.TRANSACTIONS]: TransactionReport,
    [ReportObject.RESERVATIONS]: ReservationsReport,
    [ReportObject.PURCHASES]: PurchasesReport,
    [ReportObject.RESERVATIONS_BY_LISTING]: ReservationsByListingReport,
    [ReportObject.QUESTIONNAIRE]: QuestionnaireReport,
    [ReportObject.CAPACITY]: CapacityReport,
    [ReportObject.ABANDONS]: AbandonedReport,
    [ReportObject.TICKET_REDEMPTION]: TicketRedemptionReport,
    [ReportObject.MEMBERSHIP]: MembershipsReport,
    [ReportObject.CUSTOMERS]: CustomersReport,
    [ReportObject.MERCHANDISE_GROSS_SALES]: MerchandiseGrossSalesReport,
    [ReportObject.MERCHANDISE_INVENTORY]: MerchandiseInventoryReport,
};

const MissingReport = ({ report }: { report: Report }) => {
    return (
        <div className="h-4/6 w-full space-y-4 px-6 py-10">
            <Alert color="warning">The {report.title} report has not been created yet</Alert>
            <span className="space-y-4 text-center">
                <PageNotFoundIllustration />
            </span>
        </div>
    );
};

const makeReport = (object: ReportObject, suggestedReports: SuggestedReport[]): Report | null => {
    const suggestedReport = suggestedReports.find((suggestedReport) => suggestedReport.report.object === object);

    if (suggestedReport) {
        return suggestedReport.report;
    }

    return null;
};
